import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import type { TEIFilter } from '@timeedit/ui-components';
import type { TSavedFilter } from '@timeedit/ui-components/components/Filter/Filter.type';
import {
  createFilterSelector,
  deleteFilter,
  saveFilter,
} from '../slices/persistedFilters.slice';
import { useAppDispatch, useAppSelector } from '../slices/store';

const DATE_FIELDS = ['lastLoginAt']; // Add other date fields as necessary

/**
 * Convenience hook for accessing and managing table filters that are saved in local storage.
 */
export function useSavedTableFilters(tableId: string) {
  const dispatch = useAppDispatch();

  const organizationId = useAppSelector(
    (state) => state.auth.user?.organizationId
  );

  const filterSelector = useMemo(createFilterSelector, []);
  const savedFilters = useAppSelector((state) =>
    filterSelector(state, organizationId, tableId)
  );

  const transformedSavedFilters = useMemo(() => {
    return savedFilters.map((savedFilter) => {
      const filters = savedFilter.filter.filters;
      if (!filters) {
        return savedFilter;
      }

      // Transform date string arrays to dayjs objects for specific fields
      const transformedFilters = { ...filters };

      DATE_FIELDS.forEach((field) => {
        if (filters[field] && Array.isArray(filters[field])) {
          transformedFilters[field] = (filters[field] as any[]).map((date) =>
            typeof date === 'string' ? dayjs(date) : date
          );
        }
      });

      return {
        ...savedFilter,
        filter: {
          ...savedFilter.filter,
          filters: transformedFilters,
        },
      };
    });
  }, [savedFilters]);

  const onSaveFilter = useCallback(
    async (name: string, filter: TEIFilter) => {
      if (!organizationId) return;
      dispatch(saveFilter(organizationId, tableId, name, filter));
    },
    [organizationId, tableId, dispatch]
  );

  const onDeleteFilter = useCallback(
    async (filter: TSavedFilter) => {
      if (!organizationId) return;

      dispatch(
        deleteFilter({
          organizationId,
          tableId,
          filterKey: filter.key,
        })
      );
    },
    [organizationId, tableId, dispatch]
  );

  return {
    savedFilters: transformedSavedFilters,
    onSaveFilter,
    onDeleteFilter,
  };
}
